import { useCallback, useEffect, useRef, useState } from 'react';

const PIN_THRESHOLD_PX = 48;

export function useStreamingScroll({ containerRef, bottomRef, watchDeps = [] }) {
    const pinnedRef = useRef(true);
    const [showJumpToLatest, setShowJumpToLatest] = useState(false);

    const isPinned = useCallback(() => {
        const el = containerRef?.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_THRESHOLD_PX;
    }, [containerRef]);

    const scrollToBottom = useCallback(
        (behavior = 'smooth') => {
            bottomRef?.current?.scrollIntoView?.({ behavior, block: 'end' });
        },
        [bottomRef],
    );

    const jumpToLatest = useCallback(() => {
        pinnedRef.current = true;
        setShowJumpToLatest(false);
        scrollToBottom('smooth');
    }, [scrollToBottom]);

    useEffect(() => {
        const el = containerRef?.current;
        if (!el) return undefined;

        const handleScroll = () => {
            pinnedRef.current = isPinned();
            if (pinnedRef.current) {
                setShowJumpToLatest(false);
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [containerRef, isPinned]);

    useEffect(() => {
        if (pinnedRef.current) {
            scrollToBottom(watchDeps.length ? 'auto' : 'smooth');
            setShowJumpToLatest(false);
        } else {
            setShowJumpToLatest(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, watchDeps);

    return {
        showJumpToLatest,
        jumpToLatest,
        scrollToBottom,
        isPinned,
    };
}
